import React from "react";
import { Helmet } from 'react-helmet-async';
import { Button, Card, CardBody, Divider, Textarea, addToast, Spinner, Input, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { useCart } from "../contexts/cart-context";
import { useAuth } from "../contexts/auth-context";
import ThemeContext from '../contexts/theme-context';
import api, { fetchData } from "../services/api";
import { Link } from "@heroui/react";
import { useProductDetail } from "../hooks/use-catalog";
import { useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { BASE_URL } from "../services/config";
import { pushViewProduct } from '../services/ecommerce';
import SSRDataContext from '../contexts/ssr-data-context';
import NotFoundPage from './not-found-page';
import { ErrorDisplay, LoadingDisplay } from "../components/error-boundary";
import MarkdownContent from "../components/markdown-content";
import placeholderImage from "../assets/images/image-load-failed.png";

interface ProductDetailPageProps {
  productId: string | number;
  onGoBack: () => void;
  sourceCategory?: string; // The category name user came from
  onAddToCart?: (productName: string) => void;
}

interface ProductDetail {
  id: number;
  name: string;
  category: string;
  article?: string | number;
  scent: string;
  price: number;
  description: string;
  characteristics: { name: string; value: string }[];
  notes: {
    top: string[];
    middle: string[];
    base: string[];
  };
  images: string[];
  relatedProducts: RelatedProduct[];
  // optional inventory quantity — when provided and equal to 0 means out of stock
  quantity?: number;
  is_exclusive?: boolean;
}

interface RelatedProduct {
  id: number;
  name: string;
  category: string;
  price: number;
  image: string;
}

interface Review {
  id: number;
  userName: string;
  userId?: number | string | null;
  date: string;
  rating: number;
  text: string;
  title?: string | null;
  isPending?: boolean;
  userAvatar?: string;
}

interface SeeAlsoProduct {
  id: number;
  name: string;
  article?: string;
  slug?: string;
  price: number;
  quantity?: number;
  category: string;
  image: string;
  is_exclusive?: boolean;
}

interface GiftFormState {
  recipientName: string;
  recipientEmail: string;
  requesterName: string;
  requesterEmail: string;
}

interface ReviewsSectionProps {
  reviews: Review[];
  reviewsCount: number | null;
  averageRating: number | null;
  theme?: string | null;
  isAuthenticated: boolean;
  canReview: boolean;
  isCheckingPurchase: boolean;
  reviewText: string;
  onReviewTextChange: React.Dispatch<React.SetStateAction<string>>;
  reviewRating: number;
  onReviewRatingChange: React.Dispatch<React.SetStateAction<number>>;
  isSubmitting: boolean;
  onSubmitReview: () => void;
  reviewsLoading: boolean;
}

// Format ISO datetime like "2025-10-28T15:11:27.504702Z" to readable RU string
const formatReviewDate = (iso?: string | null) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(d);
  } catch (e) {
    return String(iso);
  }
};

// Russian pluralization for the word "отзыв"
const pluralizeReviews = (count: number) => {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return 'отзывов';
  if (n1 > 1 && n1 < 5) return 'отзыва';
  if (n1 === 1) return 'отзыв';
  return 'отзывов';
};

const mapReviewResponse = (r: any, getName: (user: any) => string): Review => ({
  id: r.id,
  userName: getName(r.user),
  userId: r.user?.id ?? r.user?.pk ?? r.user_id ?? null,
  date: formatReviewDate(r.created_at || r.createdAt || r.createdAtUtc || ''),
  rating: Number(r.rating) || 0,
  text: r.body || r.text || '',
  title: r.title ?? null,
  isPending: r.approved === false,
});

export const ProductDetailPage: React.FC<ProductDetailPageProps> = ({ 
  productId, 
  onGoBack,
  sourceCategory = "каталогу", // Default text if no specific category
  onAddToCart: onAddToCartProp,
}) => {
  const [quantity, setQuantity] = React.useState(1);
  const [activeImage, setActiveImage] = React.useState(0);
  const [isImageLightboxOpen, setIsImageLightboxOpen] = React.useState(false);
  
  // Get SSR preloaded data (if any)
  const ssrData = useContext(SSRDataContext);
  const initialProduct = ssrData?.products ? ssrData.products[String(productId)] : undefined;

  // Use the product hook with actual API data and optional initial data from SSR
  const { product, isLoading, error } = useProductDetail(productId, initialProduct ?? null);

  // Note: do not early-return based on SSR initialProduct here —
  // server-side rendering already sets 404 status (see entry-server.tsx).
  // Returning early here would change the hooks order between server and client
  // and cause hydration errors like "Rendered more hooks than during the previous render".
  
  // Reviews state — will be loaded from API
  const [reviews, setReviews] = React.useState<Review[]>([]);
  const [reviewsCount, setReviewsCount] = React.useState<number | null>(null);
  const [reviewsLoading, setReviewsLoading] = React.useState(false);
  const [averageRating, setAverageRating] = React.useState<number | null>(null);
  
  // Review form state
  const [reviewText, setReviewText] = React.useState("");
  const [reviewRating, setReviewRating] = React.useState(5);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // "See also" products loaded asynchronously
  const [seeAlso, setSeeAlso] = React.useState<SeeAlsoProduct[] | null>(null);
  const [seeAlsoLoading, setSeeAlsoLoading] = React.useState(false);
  const [seeAlsoError, setSeeAlsoError] = React.useState<string | null>(null);
  const [isGiftModalOpen, setGiftModalOpen] = React.useState(false);
  const [giftForm, setGiftForm] = React.useState<GiftFormState>({
    recipientName: '',
    recipientEmail: '',
    requesterName: '',
    requesterEmail: ''
  });
  const [isGiftSubmitting, setGiftSubmitting] = React.useState(false);
  const isGiftFormValid = React.useMemo(() => {
    return Boolean(
      giftForm.recipientName.trim() &&
      giftForm.recipientEmail.trim() &&
      giftForm.requesterName.trim() &&
      giftForm.requesterEmail.trim()
    );
  }, [giftForm]);
  
  // User/auth state
  const { isAuthenticated, user } = useAuth();
  const [hasPurchased, setHasPurchased] = React.useState(false);
  const [isCheckingPurchase, setIsCheckingPurchase] = React.useState(false);
  // Theme from context to adjust styles for dark/light modes
  const { theme } = useContext(ThemeContext);
  
  // Add cart context
  const { addItem } = useCart();

  // Router hooks (move to top-level to keep hook order stable between SSR and client)
  const location = useLocation();
  const history = useHistory();

  // Breadcrumbs JSON-LD for Product detail page
  const breadcrumbJsonLd = React.useMemo(() => {
    if (!product) return null;

    const base = BASE_URL || "";
    const items: any[] = [
      {
        "@type": "ListItem",
        position: 1,
        name: "Главная",
        item: `${base}/`
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Каталог",
        item: `${base}/catalog/`
      }
    ];

    if (product.category) {
      items.push({
        "@type": "ListItem",
        position: 3,
        name: product.category,
        item: `${base}/catalog/?category=${encodeURIComponent(String((product as any).category_key || ''))}`
      });
    }

    items.push({
      "@type": "ListItem",
      position: product.category ? 4 : 3,
      name: product.name,
      item: `${base}${location?.pathname || `/product-detail/${(product as any).slug || product.id}`}`
    });

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items
    };
  }, [product, location]);

  // If product loaded and current URL doesn't use slug, replace history to canonical slug URL
  React.useEffect(() => {
    try {
      if (product && product.slug) {
        const parts = location?.pathname ? String(location.pathname).split('/') : [];
        const last = parts.length ? parts[parts.length - 1] : '';
        if (last && String(last) !== String(product.slug)) {
          // replace to avoid adding new history entry
          history.replace(`/product-detail/${product.slug}`);
        }
      }
    } catch (e) {
      // ignore
    }
  }, [product, location, history]);

  // Push product detail view event to dataLayer when product is available
  React.useEffect(() => {
    try {
      if (product) {
        pushViewProduct({ id: product.slug || product.id, name: product.name, price: product.price, category: product.category, sku: product.article });
      }
    } catch (e) {
      // ignore
    }
  }, [product]);

  // Helper to format user display name: "FirstName L." when user object provided
  const getUserDisplayName = (user: any) => {
    if (!user) return 'Пользователь';
    if (typeof user === 'string') return user;
    const first = user.first_name || user.firstName || user.name || '';
    const last = user.last_name || user.lastName || '';
    if (first && last) {
      const lastInitial = String(last).trim().charAt(0).toUpperCase();
      return `${first} ${lastInitial}.`;
    }
    if (first) return first;
    if (user.name) return user.name;
    return 'Пользователь';
  };

  // Fetch reviews from API for this product (first page)
  React.useEffect(() => {
    let cancelled = false;
    const loadReviews = async () => {
      setReviewsLoading(true);
      try {
        // Prefer slug-based reviews endpoint when available (backend uses /products/slug/<slug>/reviews/)
        const pid = String(productId);
        let reviewsUrl = '';
        if (product && product.slug) {
          reviewsUrl = `/products/slug/${encodeURIComponent(String(product.slug))}/reviews/`;
        } else if (/^\d+$/.test(pid)) {
          // numeric id fallback
          reviewsUrl = `/products/${pid}/reviews/`;
        } else {
          // treat productId as slug fallback
          reviewsUrl = `/products/slug/${encodeURIComponent(pid)}/reviews/`;
        }
        const data = await fetchData(reviewsUrl);
        // data: { count, next, previous, results: [...] }
        const results = Array.isArray(data?.results) ? data.results : [];

        // Map to local Review shape (format dates)
        const mapped: Review[] = results.map((r: any) => mapReviewResponse(r, getUserDisplayName));

        if (!cancelled) {
          setReviews(mapped);
          setReviewsCount(typeof data.count === 'number' ? data.count : mapped.length);
          // average per returned results
          if (mapped.length > 0) {
            const avg = mapped.reduce((s, it) => s + (it.rating || 0), 0) / mapped.length;
            setAverageRating(Math.round(avg * 10) / 10);
          } else {
            setAverageRating(null);
          }
        }
      } catch (err) {
        console.error('Failed to load reviews:', err);
        if (!cancelled) {
          setReviews([]);
          setReviewsCount(0);
          setAverageRating(null);
        }
      } finally {
        if (!cancelled) setReviewsLoading(false);
      }
    };

    loadReviews();
    return () => { cancelled = true; };
  }, [productId]);

  // Verify that the authenticated user actually purchased this product
  React.useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated) {
      setHasPurchased(false);
      setIsCheckingPurchase(false);
      return;
    }

    if (!product?.id) {
      setHasPurchased(false);
      setIsCheckingPurchase(false);
      return;
    }

    const numericProductId = Number(product.id);
    if (!Number.isInteger(numericProductId)) {
      setHasPurchased(false);
      setIsCheckingPurchase(false);
      return;
    }

    const verifyPurchase = async () => {
      setIsCheckingPurchase(true);
      try {
        const response = await fetchData(`/orders/has-purchased/?product=${encodeURIComponent(numericProductId)}`);
        if (!cancelled) {
          setHasPurchased(Boolean(response?.has_purchased));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to verify purchase status:', err);
          setHasPurchased(false);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingPurchase(false);
        }
      }
    };

    verifyPurchase();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, product?.id]);
  
  // Handle functions remain unchanged
  const handleQuantityChange = (change: number) => {
    setQuantity(Math.max(1, quantity + change));
  };

  const handleGiftFieldChange = (field: keyof GiftFormState, value: string) => {
    setGiftForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetGiftForm = () => {
    setGiftForm({
      recipientName: '',
      recipientEmail: '',
      requesterName: '',
      requesterEmail: ''
    });
  };

  const handleGiftSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!product) return;

    const payload = {
      product_id: product.id,
      recipient_name: giftForm.recipientName.trim(),
      recipient_email: giftForm.recipientEmail.trim(),
      requester_name: giftForm.requesterName.trim(),
      requester_email: giftForm.requesterEmail.trim()
    };

    if (!payload.recipient_name || !payload.recipient_email || !payload.requester_name || !payload.requester_email) {
      addToast({
        title: 'Заполните форму',
        description: 'Пожалуйста, укажите все данные, чтобы отправить намёк.',
        color: 'warning'
      });
      return;
    }

    setGiftSubmitting(true);
    try {
      await api.post('/gift-requests/', payload);
      addToast({
        title: 'Запрос отправлен',
        description: 'Мы сообщим получателю о вашем желании получить этот подарок.',
        color: 'success'
      });
      resetGiftForm();
      setGiftModalOpen(false);
    } catch (err) {
      console.error('Failed to submit gift request', err);
      addToast({
        title: 'Не удалось отправить',
        description: 'Попробуйте ещё раз или свяжитесь с нами другим способом.',
        color: 'danger'
      });
    } finally {
      setGiftSubmitting(false);
    }
  };

  React.useEffect(() => {
    if (!isGiftModalOpen) {
      resetGiftForm();
      setGiftSubmitting(false);
    }
  }, [isGiftModalOpen]);

  // Handle review submission
  const handleSubmitReview = async () => {
    if (!reviewText.trim() || !product) return;
    
    const payload = {
      rating: reviewRating,
      body: reviewText.trim(),
    };

    const endpoint = product.slug
      ? `/products/slug/${encodeURIComponent(String(product.slug))}/reviews/`
      : `/products/${encodeURIComponent(String(product.id))}/reviews/`;

    setIsSubmitting(true);
    try {
      const response = await api.post(endpoint, payload);
      const newReview = mapReviewResponse(response.data, getUserDisplayName);
      setReviews((prev) => [newReview, ...prev]);
      setReviewText("");
      setReviewRating(5);
      addToast({
        title: 'Отзыв отправлен',
        description: newReview.isPending
          ? 'Мы опубликуем его после проверки.'
          : 'Спасибо за ваш отзыв! Он уже на странице.',
        color: 'success',
      });
    } catch (error) {
      console.error('Failed to submit review:', error);
      addToast({
        title: 'Не удалось отправить отзыв',
        description: 'Пожалуйста, попробуйте ещё раз.',
        color: 'danger',
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const hasOwnReview = React.useMemo(() => {
    if (!user) return false;
    return reviews.some((review) => {
      if (review.userId == null) return false;
      return String(review.userId) === String(user.id);
    });
  }, [reviews, user]);

  const canSubmitReview = Boolean(isAuthenticated && hasPurchased && !hasOwnReview);

  const reviewsSectionProps: ReviewsSectionProps = {
    reviews,
    reviewsCount,
    averageRating,
    theme,
    isAuthenticated,
    canReview: canSubmitReview,
    isCheckingPurchase,
    reviewText,
    onReviewTextChange: setReviewText,
    reviewRating,
    onReviewRatingChange: setReviewRating,
    isSubmitting,
    onSubmitReview: handleSubmitReview,
    reviewsLoading,
  };

  // Load "see also" products asynchronously (independent of main product load)
  React.useEffect(() => {
    let cancelled = false;
    const loadSeeAlso = async () => {
      setSeeAlsoLoading(true);
      setSeeAlsoError(null);
      try {
        const data = await fetchData('/products/see-also/');
        const items = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];
        if (!cancelled) {
          const mapped: SeeAlsoProduct[] = items.map((p: any) => ({
            id: p.id,
            name: p.name,
            article: p.article,
            slug: p.slug,
            price: typeof p.price === 'string' ? parseFloat(p.price) : p.price || 0,
            quantity: p.quantity,
            category: typeof p.category === 'string' ? p.category : (p.category && (p.category.name || p.category.key)) || '',
            image: p.main_image || (Array.isArray(p.images) && p.images[0]?.url) || placeholderImage,
            is_exclusive: Boolean(p.is_exclusive),
          }));
          setSeeAlso(mapped);
        }
      } catch (e) {
        console.error('Failed to load see-also products', e);
        if (!cancelled) setSeeAlsoError('Не удалось загрузить рекомендации');
      } finally {
        if (!cancelled) setSeeAlsoLoading(false);
      }
    };

    loadSeeAlso();
    return () => {
      cancelled = true;
    };
  }, []);
  
  if (isLoading) {
    return (
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <LoadingDisplay message="Загрузка информации о товаре..." />
        </div>
      </section>
    );
  }
  
  if (error) {
    return (
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <ErrorDisplay 
            message="Не удалось загрузить информацию о товаре. Пожалуйста, попробуйте позже."
            onRetry={() => window.location.reload()}
          />
        </div>
      </section>
    );
  }
  
  // If product is not present (e.g. not found), show NotFound
  if (!product) {
    return <NotFoundPage />;
  }
  // Inventory state: if backend provides quantity and it's zero or less, treat as out of stock
  const isOutOfStock = product.quantity != null && Number(product.quantity) <= 0;
  const displayedQuantity = isOutOfStock ? 0 : quantity;
  const giftPreviewImage = (product.images && product.images[activeImage]) || product.images?.[0] || placeholderImage;
  
  // Define the onAddToCart function to handle both adding to cart and showing toast
  const onAddToCart = (productItem: {
    id: number;
    name: string;
    slug?: string;
    category: string;
    price: number;
    image: string;
    quantity?: number;
  }, quantity: number = 1) => {
    // Guard: if backend signals out of stock for this item, do not add
    const effectiveQuantity =
      productItem.quantity != null
        ? Number(productItem.quantity)
        : product?.quantity != null
        ? Number(product.quantity)
        : null;

    if (effectiveQuantity != null && effectiveQuantity <= 0) {
      addToast({
        title: 'Товар временно отсутствует',
        color: 'warning',
      });
      return;
    }

    // Add the item to cart
    addItem(productItem, quantity);

    // Show toast notification (global HeroUI toast)
    addToast({
      title: 'Товар добавлен в корзину',
      description: productItem.name,
      color: 'success',
    });

    // call optional parent callback (for notifications outside this component)
    if (typeof onAddToCartProp === 'function') {
      try {
        onAddToCartProp(productItem.name);
      } catch (e) {
        // ignore
      }
    }
  };

  // Helpers to adjust characteristic labels/units without transforming numeric values
  const normalizeCharacteristic = (char: { name: string; value: string }) => {
    const rawName = (char.name || '').trim();
    const lowerName = rawName.toLowerCase();

    // Exclude article from characteristics
    if (lowerName.includes('артикул') || lowerName.includes('article')) {
      return null;
    }

    // Determine display name: change label "Материал" -> "Воск"
    let displayName = rawName;
    if (lowerName === 'материал' || lowerName === 'material') {
      displayName = 'Воск';
    }

    // Keep backend value as-is, only append unit indicators when missing
    let displayValue = String(char.value ?? '').trim();

    // Volume: append "мл" if the name indicates volume and unit is missing
    if (lowerName.includes('объем') || lowerName.includes('объём') || lowerName.includes('volume')) {
      const vLower = displayValue.toLowerCase();
      if (!vLower.includes('мл') && !vLower.includes('ml') && !vLower.includes('л') && !vLower.includes('l')) {
        displayValue = displayValue ? `${displayValue} мл` : displayValue;
      }
    }

    // Weight: show as integer grams and append "г" if missing
    if (
      lowerName.includes('вес') ||
      lowerName.includes('масса') ||
      lowerName.includes('weight')
    ) {
      const numericMatch = displayValue.match(/^[0-9]+([\.,][0-9]+)?/);
      if (numericMatch) {
        const rawNumeric = numericMatch[0].replace(',', '.');
        const num = Number(rawNumeric);
        if (!isNaN(num)) {
          const intVal = Math.round(num); // always integer
          displayValue = String(intVal);
        }
      }

      const vLower = displayValue.toLowerCase();
      if (!vLower.includes('г') && !vLower.includes('гр') && !vLower.includes('g') && displayValue) {
        displayValue = `${displayValue} г`;
      }
    }

    // Burn time: normalize decimal value (no hundredths) and append "ч" when missing
    if (
      (lowerName.includes('время') && (lowerName.includes('гор') || lowerName.includes('горения'))) ||
      lowerName.includes('burn') ||
      lowerName.includes('время горения')
    ) {
      // Try to normalize numeric part: allow at most one decimal place and trim trailing ".0" / ",0"
      const numericMatch = displayValue.match(/^[0-9]+([\.,][0-9]+)?/);
      if (numericMatch) {
        const rawNumeric = numericMatch[0].replace(',', '.');
        const num = Number(rawNumeric);
        if (!isNaN(num)) {
          let normalized = num.toFixed(1); // one decimal place max
          // Drop trailing .0
          if (normalized.endsWith('.0')) {
            normalized = normalized.slice(0, -2);
          }
          // Use comma as decimal separator for RU locale
          normalized = normalized.replace('.', ',');
          // Replace only numeric part in original value
          displayValue = displayValue.replace(numericMatch[0], normalized);
        }
      }

      const vLower = displayValue.toLowerCase();
      if (!vLower.includes('ч') && !vLower.includes('час') && !vLower.includes('h') && !vLower.includes('мин') && displayValue) {
        displayValue = `${displayValue} ч`;
      }
    }

    // Determine kind for grouping
    let kind: 'weight' | 'volume' | 'burn' | 'wax' | 'scent' | 'hardware' | 'other' = 'other';
    if (lowerName.includes('вес') || lowerName.includes('масса') || lowerName.includes('weight')) {
      kind = 'weight';
    } else if (lowerName.includes('объем') || lowerName.includes('объём') || lowerName.includes('volume')) {
      kind = 'volume';
    } else if (
      (lowerName.includes('время') && (lowerName.includes('гор') || lowerName.includes('горения'))) ||
      lowerName.includes('burn') ||
      lowerName.includes('время горения')
    ) {
      kind = 'burn';
    } else if (displayName.toLowerCase() === 'воск' || lowerName === 'материал' || lowerName === 'material') {
      kind = 'wax';
    } else if (displayName.toLowerCase() === 'аромат' || lowerName.includes('аромат') || lowerName.includes('scent')) {
      kind = 'scent';
    } else if (
      lowerName.includes('фитиль') ||
      lowerName.includes('подсвечник') ||
      lowerName.includes('крышк') ||
      lowerName.includes('стакан') ||
      lowerName.includes('holder') ||
      lowerName.includes('lid') ||
      lowerName.includes('cap')
    ) {
      kind = 'hardware';
    }

    return {
      name: displayName,
      value: displayValue,
      kind,
    };
  };
  
  // Merge aroma into characteristics list so it renders as a characteristic
  const characteristicsForRender = [
    ...(product.scent ? [{ name: 'Аромат', value: product.scent }] : []),
    ...(product.characteristics || []),
  ];

  // Location for building canonical and json-ld url

  // Open Graph image: используем первую картинку товара, если есть
  const ogImage = (product.images && product.images.length > 0)
    ? product.images[0]
    : undefined;

  return (
    <>
      <Helmet>
        <title>{product.name ? `${product.name} — SAFONOVA` : 'Товар — SAFONOVA'}</title>
        <meta name="description" content={product.description || product.scent || 'Подробности товара'} />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || `/product-detail/${product.id}`}`} />
        <meta property="og:title" content={product.name ? `${product.name} — SAFONOVA` : 'Товар — SAFONOVA'} />
        <meta property="og:description" content={product.description || product.scent || 'Подробности товара'} />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || `/product-detail/${product.id}`}`} />
        {ogImage && <meta property="og:image" content={ogImage} />}
      </Helmet>
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          {/* Back to Category Button */}
          <div className="max-w-7xl mx-auto mb-8">
            {/* <Link 
              href="#"
              className="flex items-center gap-2 text-primary hover:underline hover:brightness-110 transition-all"
              onClick={(e) => {
                e.preventDefault();
                onGoBack();
              }}
            >
              <Icon icon="lucide:arrow-left" width={16} />
              Назад к {sourceCategory}
            </Link> */}
            <Button
              variant="flat"
              color="default"
              startContent={<Icon icon="lucide:arrow-left" width={18} />}
              onClick={(e) => {
                e.preventDefault();
                onGoBack();
              }}>
                Назад к {sourceCategory}
              </Button>
          </div>
          
          {/* Product Detail Content */}
          <div className="max-w-7xl mx-auto">
            {/* Product Detail Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Product Images */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative h-[500px] mb-4 cursor-zoom-in" onClick={() => setIsImageLightboxOpen(true)}>
                  {product.is_exclusive && (
                    <div className="absolute left-3 top-3 z-20 inline-flex items-center gap-1 rounded-full bg-purple-700/95 text-white px-3 py-1 text-xs font-medium tracking-wide uppercase shadow-sm">
                      <Icon icon="lucide:sparkles" width={14} />
                      <span>Эксклюзив</span>
                    </div>
                  )}
                  {isOutOfStock && (
                    <div className="absolute left-3 top-3 z-20 bg-red-600 text-white px-3 py-1 rounded-md text-sm">
                      Нет в наличии
                    </div>
                  )}
                  <img 
                    src={product.images?.[activeImage] || ''} 
                    alt={product.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex gap-4">
                  {(product.images || []).map((image, index) => (
                    <button 
                      key={index}
                      className={`w-20 h-20 border ${activeImage === index ? 'border-primary' : 'border-divider'}`}
                      onClick={() => setActiveImage(index)}
                    >
                      <img 
                        src={image} 
                        alt={`${product.name} ${index + 1}`} 
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
                <div className="hidden lg:block mt-10">
                  <ReviewsSection {...reviewsSectionProps} />
                </div>
              </motion.div>

              {/* Product Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="mb-2 text-sm text-foreground/60 flex items-center justify-between">
                  <div className="truncate mr-4">
                    <span className="text-foreground/50 mr-2">Категория: {product.category}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {product.article && (
                      <div className="text-foreground/50 text-right whitespace-nowrap">Артикул: {product.article}</div>
                    )}
                    <div className="flex items-center gap-1 text-xs font-medium whitespace-nowrap">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          isOutOfStock ? 'bg-red-500' : 'bg-emerald-500'
                        }`}
                      />
                      <span
                        className={
                          isOutOfStock
                            ? 'text-red-500/80'
                            : 'text-emerald-600/80'
                        }
                      >
                        {isOutOfStock ? 'Нет в наличии' : 'В наличии'}
                      </span>
                    </div>
                  </div>
                </div>
                <h1 className="text-3xl md:text-4xl font-light mb-4">{product.name}</h1>

                {/* Price (rating moved to reviews header) */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <span className="text-4xl md:text-5xl font-extrabold leading-none">{new Intl.NumberFormat('ru-RU').format(product.price || 0)}</span>
                    <span className="ml-2 text-lg text-foreground/70">₽</span>
                  </div>
                </div>

                {/* Aroma moved into characteristics below */}
                
                <div className="mb-8">
                  <h2 className="text-lg font-medium mb-3 flex items-center gap-2">
                    <span>Описание</span>
                  </h2>
                  <MarkdownContent content={product.description} />
                </div>

                <Divider className="my-8" />

                <div className="flex items-center gap-4 mb-8 flex-wrap">
                  <div className="flex items-center border border-divider">
                    <Button 
                      isIconOnly 
                      size="sm" 
                      variant="flat" 
                      onPress={() => !isOutOfStock && handleQuantityChange(-1)}
                      isDisabled={isOutOfStock}
                      className="rounded-none"
                    >
                      <Icon icon="lucide:minus" width={16} />
                    </Button>
                    <span className="px-6">{displayedQuantity}</span>
                    <Button 
                      isIconOnly 
                      size="sm" 
                      variant="flat" 
                      onPress={() => !isOutOfStock && handleQuantityChange(1)}
                      isDisabled={isOutOfStock}
                      className="rounded-none"
                    >
                      <Icon icon="lucide:plus" width={16} />
                    </Button>
                  </div>
                  <div className="flex flex-1 gap-3 min-w-[240px]">
                    {isOutOfStock ? (
                      <Button
                        color="default"
                        size="lg"
                        className="flex-grow"
                        isDisabled={true}
                      >
                        Нет в наличии
                      </Button>
                    ) : (
                      <Button 
                        color="primary" 
                        size="lg"
                        className="flex-grow"
                        endContent={<Icon icon="lucide:shopping-bag" width={18} />}
                        onPress={() => onAddToCart({
                          id: product.id,
                          name: product.name,
                          slug: (product as any).slug,
                          category: product.category,
                          price: product.price,
                          image: product.images[0],  // Use the first image
                          quantity: product.quantity,
                        }, quantity)}
                      >
                        В корзину
                      </Button>
                    )}
                    <Button
                      variant="bordered"
                      color="primary"
                      size="lg"
                      className="flex-grow"
                      endContent={<Icon icon="lucide:gift" width={18} />}
                      onPress={() => setGiftModalOpen(true)}
                    >
                      Хочу в подарок
                    </Button>
                  </div>
                </div>

                <Divider className="my-8" />
                {/* Scent Pyramid: показываем прямо над характеристиками в правом блоке */}
                <div className="mb-8">
                  <h2 className="text-2xl font-light mb-4">Пирамида <span className="font-medium">аромата</span></h2>
                  <p className="text-foreground/70 mb-6">Раскройте для себя сложную композицию этого изысканного аромата</p>

                  {product.is_exclusive && (
                    <div className="mb-6 rounded-lg border border-purple-500/25 bg-purple-500/10 px-4 py-3 flex items-start gap-3">
                      <div className="mt-0.5">
                        <Icon icon="lucide:sparkles" className="text-purple-400" width={18} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground mb-1">Эксклюзивный аромат от основателя бренда</h3>
                        <p className="text-sm text-foreground/80">
                          В этом товаре используется авторский аромат, созданный основателем бренда Анной Сафоновой.
                          Такой композиции вы не найдёте ни у одного другого бренда.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card className="border border-divider bg-background" disableRipple>
                      <CardBody className="p-4 text-center">
                        <div className="bg-primary/10 p-2 rounded-full w-10 h-10 flex items-center justify-center mx-auto mb-3">
                          <Icon icon="lucide:cloud" className="text-primary" width={20} />
                        </div>
                        <h4 className="text-sm font-medium mb-2">Верхние ноты</h4>
                        <p className="text-foreground/70 text-xs mb-2">Первое впечатление</p>
                        <div className="flex flex-wrap justify-center gap-2">
                          {(product.notes?.top || []).map((note, index) => (
                            <span key={index} className="px-2 py-1 bg-content2 text-sm rounded-full">{note}</span>
                          ))}
                        </div>
                      </CardBody>
                    </Card>

                    <Card className="border border-divider bg-background" disableRipple>
                      <CardBody className="p-4 text-center">
                        <div className="bg-primary/10 p-2 rounded-full w-10 h-10 flex items-center justify-center mx-auto mb-3">
                          <Icon icon="lucide:heart" className="text-primary" width={20} />
                        </div>
                        <h4 className="text-sm font-medium mb-2">Средние ноты</h4>
                        <p className="text-foreground/70 text-xs mb-2">Сердце аромата</p>
                        <div className="flex flex-wrap justify-center gap-2">
                          {(product.notes?.middle || []).map((note, index) => (
                            <span key={index} className="px-2 py-1 bg-content2 text-sm rounded-full">{note}</span>
                          ))}
                        </div>
                      </CardBody>
                    </Card>

                    <Card className="border border-divider bg-background" disableRipple>
                      <CardBody className="p-4 text-center">
                        <div className="bg-primary/10 p-2 rounded-full w-10 h-10 flex items-center justify-center mx-auto mb-3">
                          <Icon icon="lucide:anchor" className="text-primary" width={20} />
                        </div>
                        <h4 className="text-sm font-medium mb-2">Базовые ноты</h4>
                        <p className="text-foreground/70 text-xs mb-2">Стойкое послевкусие</p>
                        <div className="flex flex-wrap justify-center gap-2">
                          {(product.notes?.base || []).map((note, index) => (
                            <span key={index} className="px-2 py-1 bg-content2 text-sm rounded-full">{note}</span>
                          ))}
                        </div>
                      </CardBody>
                    </Card>
                  </div>

                  <Divider className="my-6" />

                  <h3 className="text-lg font-medium mb-4">Характеристики</h3>
                  {(() => {
                    const normalized = characteristicsForRender
                      .map(normalizeCharacteristic)
                      .filter(Boolean) as Array<{ name: string; value: string; kind: string }>;

                    const row1 = normalized.filter(c => ['weight', 'volume', 'burn'].includes(c.kind));
                    const row2 = normalized.filter(c => ['wax', 'scent'].includes(c.kind));
                    const row3 = normalized.filter(c => c.kind === 'hardware');

                    const used = new Set<string>();
                    const markUsed = (items: typeof normalized) => {
                      items.forEach(c => used.add(`${c.kind}|${c.name}|${c.value}`));
                    };
                    markUsed(row1);
                    markUsed(row2);
                    markUsed(row3);

                    const rest = normalized.filter(c => !used.has(`${c.kind}|${c.name}|${c.value}`));

                    const renderRow = (items: typeof normalized, keyPrefix: string) => {
                      if (!items.length) return null;
                      return (
                        <div className="flex flex-wrap gap-4 mb-3" key={keyPrefix}>
                          {items.map((char, index) => (
                            <div
                              key={`${keyPrefix}-${index}-${char.name}`}
                              className="bg-content2/50 p-4 border border-divider min-w-[140px] flex-1"
                            >
                              <p className="text-sm text-foreground/70 mb-1">{char.name}</p>
                              <p className="font-medium">{char.value}</p>
                            </div>
                          ))}
                        </div>
                      );
                    };

                    return (
                      <div>
                        {renderRow(row1, 'row-main')}
                        {renderRow(row2, 'row-aroma')}
                        {renderRow(row3, 'row-hardware')}
                        {renderRow(rest, 'row-other')}
                      </div>
                    );
                  })()}

                  {/* Delivery and Payment block */}
                  <div className="mt-8 border border-divider bg-content2/40 p-0">
                    <div className="px-4 sm:px-5 pt-4 sm:pt-5 mb-3">
                      <h3 className="text-base font-medium mb-2">Доставка и возврат</h3>
                    </div>
                    <div className="flex items-start gap-3 mb-4 px-4 sm:px-5">
                      <div className="bg-primary/10 p-2 rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0">
                        <Icon icon="lucide:truck" className="text-primary" width={18} />
                      </div>
                      <div className="text-sm text-foreground/70">
                        Доставка осуществляется по всей России, сроки зависят от региона и выбранного способа доставки.
                      </div>
                    </div>
                    <div className="flex flex-col gap-0">
                      <a
                        href="/shipping"
                        className="w-full text-sm font-medium px-4 py-2 bg-background hover:bg-content2 transition-colors border-t border-divider text-left flex items-center justify-between gap-2"
                      >
                        <span>Подробнее про доставку и оплату</span>
                        <Icon icon="lucide:arrow-up-right" className="text-foreground/40" width={16} />
                      </a>
                      <a
                        href="/returns"
                        className="w-full text-sm font-medium px-4 py-2 bg-background hover:bg-content2 transition-colors border-t border-divider text-left flex items-center justify-between gap-2"
                      >
                        <span>Подробнее про условия возврата</span>
                        <Icon icon="lucide:arrow-up-right" className="text-foreground/40" width={16} />
                      </a>
                    </div>
                  </div>

                  <div className="mt-10 lg:hidden">
                    <ReviewsSection {...reviewsSectionProps} />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* See Also Section */}
      <section className="py-16 bg-content2/40">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-light">
                  Смотрите <span className="font-medium">также</span>
                </h2>
                <p className="text-foreground/70 text-sm md:text-base mt-2">
                  Другие товары, которые могут вам понравиться
                </p>
              </div>
            </div>

            {seeAlsoLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="border border-divider bg-content2/40 animate-pulse h-64"
                  />
                ))}
              </div>
            )}

            {!seeAlsoLoading && seeAlsoError && (
              <div className="text-sm text-foreground/60">
                {seeAlsoError}
              </div>
            )}

            {!seeAlsoLoading && !seeAlsoError && seeAlso && seeAlso.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {seeAlso.map((item) => (
                  <Card
                    key={item.id}
                    isPressable
                    className="border border-divider bg-content2/50 product-card product-card-hover cursor-pointer"
                    disableRipple
                    onPress={() => {
                      if (item.slug) {
                        history.push(`/product-detail/${item.slug}`);
                      } else {
                        history.push(`/product-detail/${item.id}`);
                      }
                    }}
                  >
                    <CardBody className="p-0 overflow-hidden">
                      <div className="relative h-40 overflow-hidden bg-content2">
                        {item.is_exclusive && (
                          <div className="absolute left-2 top-2 z-20 inline-flex items-center gap-1 rounded-full bg-purple-700/90 text-white px-2.5 py-1 text-[10px] font-medium tracking-wide uppercase shadow-sm">
                            <Icon icon="lucide:sparkles" width={12} />
                            <span>Эксклюзив</span>
                          </div>
                        )}
                        <img
                          src={item.image || placeholderImage}
                          alt={item.name}
                          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        />
                      </div>
                    </CardBody>
                    <Divider />
                    <CardBody className="flex flex-col items-start gap-2 p-4">
                      <p className="text-xs text-foreground/60 uppercase tracking-wide mb-1">
                        {item.category}
                      </p>
                      <h3 className="text-sm font-medium line-clamp-2">
                        {item.name}
                      </h3>
                      <div className="flex items-center justify-between w-full pt-3">
                        <div className="text-base font-semibold">
                          {new Intl.NumberFormat('ru-RU').format(item.price || 0)} ₽
                        </div>
                        <Button
                          size="sm"
                          variant="flat"
                          className="px-3 py-1 text-xs font-medium bg-background/60 hover:bg-primary hover:text-primary-foreground transition-colors"
                          endContent={<Icon icon="lucide:shopping-bag" width={16} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddToCart({
                              id: item.id,
                              name: item.name,
                              slug: item.slug,
                              category: item.category,
                              price: item.price,
                              image: item.image,
                              quantity: item.quantity,
                            });
                          }}
                        >
                          В корзину
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Modal
        isOpen={isGiftModalOpen}
        onOpenChange={setGiftModalOpen}
        size="4xl"
        backdrop="opaque"
        placement="center"
        classNames={{ base: 'rounded-none max-w-5xl w-full' }}
      >
        <ModalContent>
          {(onClose) => (
            <form onSubmit={handleGiftSubmit}>
              <ModalHeader className="flex flex-col items-start gap-1">
                <p className="text-xs uppercase tracking-[0.4em] text-foreground/50">Подарочный намёк</p>
                <h2 className="text-2xl font-light">Поделитесь желанием получить <span className="font-semibold">{product.name}</span></h2>
              </ModalHeader>
              <ModalBody>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/3">
                    <div className="border border-divider bg-content1 h-full min-h-[320px] flex items-center justify-center p-2">
                      <img
                        src={giftPreviewImage}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="md:flex-1 space-y-4">
                    <p className="text-sm text-foreground/80">
                      Заполните данные, и мы отправим деликатное письмо человеку, которому вы хотите намекнуть о своём желании получить этот товар.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        label="Кому намекнуть"
                        placeholder="Имя получателя"
                        variant="bordered"
                        value={giftForm.recipientName}
                        onValueChange={(value) => handleGiftFieldChange('recipientName', value)}
                        classNames={{ inputWrapper: 'rounded-none' }}
                      />
                      <Input
                        label="Email получателя"
                        type="email"
                        placeholder="example@mail.com"
                        variant="bordered"
                        value={giftForm.recipientEmail}
                        onValueChange={(value) => handleGiftFieldChange('recipientEmail', value)}
                        classNames={{ inputWrapper: 'rounded-none' }}
                      />
                      <Input
                        label="От кого письмо"
                        placeholder="Ваше имя"
                        variant="bordered"
                        value={giftForm.requesterName}
                        onValueChange={(value) => handleGiftFieldChange('requesterName', value)}
                        classNames={{ inputWrapper: 'rounded-none' }}
                      />
                      <Input
                        label="Ваш email"
                        type="email"
                        placeholder="you@mail.com"
                        variant="bordered"
                        value={giftForm.requesterEmail}
                        onValueChange={(value) => handleGiftFieldChange('requesterEmail', value)}
                        classNames={{ inputWrapper: 'rounded-none' }}
                      />
                    </div>
                    <p className="text-xs text-foreground/60">
                      Мы отправим аккуратное письмо с ссылкой на этот товар и вашим именем, без дополнительных сообщений.
                    </p>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter className="justify-between flex-col sm:flex-row gap-3">
                <Button
                  variant="light"
                  type="button"
                  className="rounded-none w-full sm:w-auto"
                  onPress={() => {
                    resetGiftForm();
                    setGiftModalOpen(false);
                    onClose();
                  }}
                >
                  Закрыть
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  className="rounded-none w-full sm:w-auto"
                  isLoading={isGiftSubmitting}
                  isDisabled={!isGiftFormValid || isGiftSubmitting}
                >
                  Отправить намёк
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>

      {/* Image lightbox */}
      {isImageLightboxOpen && product.images && product.images.length > 0 && product.images[activeImage] && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setIsImageLightboxOpen(false)}
        >
          <button
            type="button"
            aria-label="Закрыть изображение"
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              setIsImageLightboxOpen(false);
            }}
          >
            <Icon icon="lucide:x" width={24} />
          </button>
          {product.images.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Предыдущее изображение"
                className="absolute left-4 md:left-8 text-white/80 hover:text-white p-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImage((prev) => {
                    const total = product.images?.length || 0;
                    return total ? (prev - 1 + total) % total : prev;
                  });
                }}
              >
                <Icon icon="lucide:chevron-left" width={28} />
              </button>
              <button
                type="button"
                aria-label="Следующее изображение"
                className="absolute right-4 md:right-8 text-white/80 hover:text-white p-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImage((prev) => {
                    const total = product.images?.length || 0;
                    return total ? (prev + 1) % total : prev;
                  });
                }}
              >
                <Icon icon="lucide:chevron-right" width={28} />
              </button>
            </>
          )}
          <div className="max-w-5xl max-h-[90vh] px-4">
            <img
              src={product.images[activeImage]}
              alt={product.name}
              className="w-full h-full object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Toast notifications теперь идут через глобальный addToast из HeroUI */}
    </>
  );
};

const ReviewsSection: React.FC<ReviewsSectionProps> = ({
  reviews,
  reviewsCount,
  averageRating,
  theme,
  isAuthenticated,
  canReview,
  isCheckingPurchase,
  reviewText,
  onReviewTextChange,
  reviewRating,
  onReviewRatingChange,
  isSubmitting,
  onSubmitReview,
  reviewsLoading,
}) => {
  const count = typeof reviewsCount === 'number' ? reviewsCount : reviews.length;
  const showSummary = averageRating != null && count > 0;

  const renderStars = (rating: number) => (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <Icon 
          key={star} 
          icon="lucide:star" 
          className={star <= rating ? "text-yellow-400" : "text-foreground/30"} 
          width={18} 
        />
      ))}
    </div>
  );

  return (
    <section id="reviews" className="py-8 bg-background">
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-light mb-2 text-left">
            Отзывы <span className="font-medium">покупателей</span>
          </h2>
          {showSummary && (
            <div className="text-right">
              <div className="flex items-center gap-3 justify-end">
                <div className="flex items-center bg-yellow-50 border border-yellow-100 rounded-md px-3 py-2">
                  <Icon icon="lucide:star" className="text-yellow-400" width={18} />
                  <div className={`ml-2 text-lg font-semibold ${theme === 'dark' ? 'text-black' : 'text-foreground'}`}>
                    {averageRating}
                  </div>
                  <div className={`ml-1 text-sm ${theme === 'dark' ? 'text-black/60' : 'text-foreground/60'}`}>
                    /5
                  </div>
                </div>
                <div className="text-sm text-foreground/70">{`${count} ${pluralizeReviews(count)}`}</div>
              </div>
            </div>
          )}
        </div>

        {isAuthenticated && (isCheckingPurchase || canReview) && (
          <Card className="border border-divider mb-6">
            <CardBody className="p-4">
              <h3 className="text-lg font-medium mb-3">Оставьте свой отзыв</h3>
              {isCheckingPurchase ? (
                <div className="flex items-center gap-2 text-sm text-foreground/70">
                  <Spinner size="sm" />
                  <span>Проверяем, покупали ли вы этот товар...</span>
                </div>
              ) : canReview ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground/70">Ваша оценка:</span>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          className="focus:outline-none"
                          onClick={() => onReviewRatingChange(star)}
                        >
                          <Icon 
                            icon="lucide:star" 
                            className={star <= reviewRating ? "text-yellow-400" : "text-foreground/30"} 
                            width={22} 
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <Textarea
                    label="Ваш отзыв"
                    placeholder="Поделитесь своими впечатлениями о продукте..."
                    value={reviewText}
                    onValueChange={(value) => onReviewTextChange(value)}
                    minRows={3}
                    maxRows={6}
                    variant="bordered"
                    className="w-full"
                  />
                  <Button
                    color="primary"
                    isLoading={isSubmitting}
                    isDisabled={!reviewText.trim() || isSubmitting}
                    onPress={onSubmitReview}
                  >
                    Отправить отзыв
                  </Button>
                </div>
              ) : null}
            </CardBody>
          </Card>
        )}

        {reviewsLoading ? (
          <div className="text-sm text-foreground/70">Загрузка отзывов...</div>
        ) : reviews.length > 0 ? (
          <div className="space-y-4">
            {reviews.map((review) => (
              <Card key={review.id} className="border border-divider bg-content2/50">
                <CardBody className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex gap-2 items-center">
                      <div className="text-sm font-medium">{review.userName}</div>
                      <div className="text-xs text-foreground/60">{review.date}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {renderStars(Math.round(review.rating))}
                      <div className="text-sm text-foreground/60">{review.rating}</div>
                      {review.isPending && (
                        <span className="text-[10px] uppercase tracking-wide text-amber-600 border border-amber-200 rounded px-2 py-0.5">
                          На модерации
                        </span>
                      )}
                    </div>
                  </div>
                  {review.title && (
                    <p className="text-sm font-medium text-foreground mb-1">{review.title}</p>
                  )}
                  <p className="text-foreground/80 text-sm">{review.text}</p>
                </CardBody>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon icon="lucide:message-square" className="mx-auto mb-4 text-foreground/40" width={48} />
            <h3 className="text-lg font-medium mb-2">Пока нет отзывов</h3>
            <p className="text-foreground/70 mb-6">Будьте первым, кто оставит отзыв об этом продукте</p>
          </div>
        )}
      </div>
    </section>
  );
};